import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/** @typedef {'onetime' | 'subscription'} PurchaseType */

/**
 * @typedef {Object} SubscriptionPickerRefs
 * @property {HTMLInputElement} [oneTimeRadio]
 * @property {HTMLInputElement} [subscriptionRadio]
 * @property {HTMLUListElement} [planOptions]
 */

/**
 * @extends {Component<SubscriptionPickerRefs>}
 */
class SubscriptionPickerComponent extends Component {
  /** @type {HTMLElement | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#syncFormInput();
    this.#dispatchPriceUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getSellingPlanInput() {
    const section = this.closest('.shopify-section, dialog');
    const productId = this.dataset.productId;
    const form =
      section?.querySelector(`product-form-component[data-product-id="${productId}"]`) ??
      section?.querySelector('product-form-component');
    return form?.querySelector('[ref="sellingPlanId"]') ?? null;
  }

  /**
   * @returns {PurchaseType}
   */
  #getPurchaseType() {
    const { subscriptionRadio } = this.refs;
    return subscriptionRadio?.checked ? 'subscription' : 'onetime';
  }

  /**
   * @returns {string}
   */
  #getMoneyFormat() {
    return this.dataset.moneyFormat || '{{amount}}';
  }

  /**
   * @returns {string}
   */
  #getCurrency() {
    return this.dataset.currency || 'USD';
  }

  /**
   * @param {number} priceCents
   * @returns {string}
   */
  #formatPrice(priceCents) {
    return formatMoney(priceCents, this.#getMoneyFormat(), this.#getCurrency());
  }

  /**
   * @returns {HTMLLIElement | null}
   */
  #getSelectedPlanItem() {
    const { planOptions } = this.refs;
    return (
      planOptions?.querySelector('.shavingart__sub-pill.is-selected, .collections-list-category-filter.is-selected') ??
      planOptions?.querySelector('.shavingart__sub-pill') ??
      planOptions?.querySelector('.collections-list-category-filter') ??
      null
    );
  }

  /**
   * @returns {{ price: number, compareAtPrice: number } | null}
   */
  #getSelectedAllocationPrices() {
    const item = this.#getSelectedPlanItem();
    if (!item) return null;

    const price = Number(item.dataset.price);
    const compareAtPrice = Number(item.dataset.compareAtPrice);

    return {
      price: Number.isFinite(price) ? price : 0,
      compareAtPrice: Number.isFinite(compareAtPrice) ? compareAtPrice : 0,
    };
  }

  /**
   * @returns {string}
   */
  #getSelectedPlanId() {
    return this.#getSelectedPlanItem()?.dataset.planId ?? '';
  }

  #syncSpacerLabel() {
    const item = this.#getSelectedPlanItem();
    const label = item?.querySelector('.collections-list-filter-label')?.textContent;
    const spacerLabel = this.querySelector('.collections-list-filters-spacer .collections-list-filter-label');
    if (label && spacerLabel) {
      spacerLabel.textContent = label;
    }
  }

  #syncFormInput() {
    const input = this.#getSellingPlanInput();
    if (!input) return;

    const fieldset = this.querySelector('.shavingart__subscription-picker__fieldset');
    if (!fieldset) {
      input.value = '';
      input.disabled = true;
      return;
    }

    const purchaseType = this.#getPurchaseType();
    const planId = this.#getSelectedPlanId();

    if (purchaseType === 'subscription' && planId) {
      input.value = planId;
      input.disabled = false;
    } else {
      input.value = '';
      input.disabled = true;
    }

    this.#syncSpacerLabel();
  }

  #dispatchPriceUpdate() {
    const purchaseType = this.#getPurchaseType();
    const variantPrice = Number(this.dataset.variantPrice);

    /** @type {{ purchaseType: PurchaseType, price: number, formattedPrice: string, productId: string }} */
    const detail = {
      purchaseType,
      price: variantPrice,
      formattedPrice: this.#formatPrice(variantPrice),
      productId: this.dataset.productId ?? '',
    };

    if (purchaseType === 'subscription') {
      const allocation = this.#getSelectedAllocationPrices();
      if (allocation) {
        detail.price = allocation.price;
        detail.formattedPrice = this.#formatPrice(allocation.price);
      }
    }

    this.dispatchEvent(
      new CustomEvent(ThemeEvents.subscriptionUpdate, {
        bubbles: true,
        detail,
      })
    );
  }

  #disableSellingPlanInput() {
    const input = this.#getSellingPlanInput();
    if (!input) return;
    input.value = '';
    input.disabled = true;
  }

  /**
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (String(event.detail.data.productId) !== this.dataset.productId) {
      return;
    }

    const variantPrice = event.detail.resource?.price;
    if (typeof variantPrice === 'number') {
      this.dataset.variantPrice = String(variantPrice);
    }

    const newPicker = event.detail.data.html.querySelector(
      `subscription-picker-component[data-block-id="${this.dataset.blockId}"]`
    );

    const fieldsetInResponse = newPicker?.querySelector('.shavingart__subscription-picker__fieldset');
    const fieldsetInDom = this.querySelector('.shavingart__subscription-picker__fieldset');
    const variantAvailable = event.detail.resource?.available !== false;

    if (!newPicker) {
      this.#disableSellingPlanInput();
      this.remove();
      return;
    }

    if (!event.detail.data.newProduct && newPicker instanceof HTMLElement && newPicker.dataset.productId) {
      this.dataset.productId = newPicker.dataset.productId;
    }

    // After updateMain(), nodes may already be moved from parsed HTML into the live DOM,
    // leaving newPicker empty and a second morph would incorrectly clear the picker.
    if (!fieldsetInResponse && fieldsetInDom && variantAvailable) {
      this.updatedCallback();
      this.#syncFormInput();
      this.#dispatchPriceUpdate();
      return;
    }

    if (!fieldsetInResponse && !fieldsetInDom) {
      this.#syncFormInput();
      this.#dispatchPriceUpdate();
      return;
    }

    morph(this, newPicker);
    this.updatedCallback();
    this.#syncFormInput();
    this.#dispatchPriceUpdate();
  };

  /** Re-syncs selling plan input after combined listing main morph. */
  resync() {
    this.#syncFormInput();
    this.#dispatchPriceUpdate();
  }

  handlePurchaseTypeChange() {
    this.#syncFormInput();
    this.#dispatchPriceUpdate();
  }

  /**
   * @param {Event} event
   */
  handlePlanClick(event) {
    const item = event.target.closest('li');
    const filterGroup = event.target.closest('[data-type="plan"]');
    if (!item || !filterGroup || !item.dataset.planId) return;

    // The pill layout marks selection with a class rather than by reordering, so the
    // cadence order stays stable as the customer clicks between options. Fall back to
    // the legacy dropdown list when that markup is present instead.
    const pillList = filterGroup.matches('[ref="planOptions"]')
      ? filterGroup
      : filterGroup.querySelector('[ref="planOptions"]');
    const list = pillList ?? filterGroup.querySelector('.collections-list-filters-options');

    const { subscriptionRadio } = this.refs;
    if (subscriptionRadio) {
      subscriptionRadio.checked = true;
    }

    if (list && item.parentElement === list) {
      if (list.classList.contains('shavingart__sub-pills')) {
        for (const pill of list.children) pill.classList.toggle('is-selected', pill === item);
      } else {
        list.prepend(item);
      }
    }

    list?.classList.remove('is-open');
    filterGroup.querySelector('ul')?.blur();
    this.#syncFormInput();
    this.#dispatchPriceUpdate();
  }
}

if (!customElements.get('subscription-picker-component')) {
  customElements.define('subscription-picker-component', SubscriptionPickerComponent);
}
